import { Company } from 'module/api/company/types';
import { useTranslation } from 'module/hooks/use-translation';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Popover } from 'module/ui/components/atoms/popover';
import { FilterBar } from 'module/ui/components/molecules/filter-bar';
import { HelperText01, Short01 } from 'module/ui/components/styles';
import { faEnvelope } from '@fortawesome/pro-duotone-svg-icons';
import {
  faEdit,
  // faEllipsisVertical,
  // faFolder,
  faPage,
  faSidebar,
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Button, EmptyState as RocketlaneEmptyState } from '@rocketlane/ui';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { PageType } from 'api/page-template/types';
import moment from 'moment';
import { useDeletePage } from 'queries/pages/use-delete-page';
import React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { MetadataTooltip } from 'components/common/metadata-tooltip';
import {
  LoadingWrapper,
  Sidebar,
  SidebarInPopover,
  PageWrapper,
  PageContent,
  MainContent,
  SecondaryFilter,
  SidebarMenu,
} from 'components/organisms/pages/shared/styles';
import { useEmailPagesRouteNavigations } from './route-navigations';
import { useCurrentEmailPage } from './use-current-email-page';
import { useEmailPagesList } from './use-email-pages-list';
import { ChatProvider, useChatContext } from '../chat/chat-context';
import { ChatSidebar } from '../chat/chat-sidebar';
import { useChatTrigger } from '../chat/use-chat-trigger';
import { Document } from '../document/document';

const RocketlaneEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: var(--spacing-09) var(--spacing-08);
`;

const EmailListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow: visible;
  justify-content: center;
`;
const EmailListItemTitle = styled.div`
  ${Short01};
  color: var(--text-primary);
  width: 100%;
  flex-shrink: 0;
  display: block;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: 20px;
`;
const EmailListItemDescription = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
  width: 100%;
  flex-shrink: 0;
`;

export type EmailPagesPermissions = {
  canReadEmailPages: boolean;
  canWriteEmailPages?: boolean;
  canDeleteEmailPages: boolean;
  canReadEmailPageTemplates: boolean;
  canCreateEmailPageTemplates: boolean;
  canWriteEmailPageTemplates: boolean;
  canDeleteEmailPageTemplates: boolean;
  canWriteEmailIntelligenceSettings?: boolean;
};

interface AccountEmailsProps {
  company: Company;
  projectId?: number;
  emailPermissions?: EmailPagesPermissions;
  isModuleFed?: boolean;
}
const AccountEmailsContent = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
}: AccountEmailsProps) => {
  const intl = useIntl();
  const { value, onChange, clear } = useSearchValueState();
  const { emailPages, isEmailPagesLoading, allEmailPages } = useEmailPagesList({
    company,
    searchText: value,
  });
  const { emailPage, isEmailPageLoading } = useCurrentEmailPage({
    company,
    emailPages,
    isEmailPagesLoading,
    projectId,
  });

  const { navigateToList, navigateToEmail, navigateToEmailEdit } =
    useEmailPagesRouteNavigations(projectId);

  const emailPageDocumentMappingId =
    emailPage?.metadata.documentSourceMappingModel?.pageDocumentMappingId;
  const emailDocumentId = emailPage?.metadata.documentId;

  // Use allEmailPages (full dataset), not emailPages (search-filtered).
  // With active search, emailPages can be empty despite other pages existing,
  // which would incorrectly trigger navigateToList and drop context.
  const allEmailAssociations = useMemo(
    () => allEmailPages.flatMap((page) => page.associations),
    [allEmailPages],
  );

  const { handleDelete } = useDeletePage({
    pageDocumentMappingId: emailPageDocumentMappingId,
    documentId: emailDocumentId,
    pageType: PageType.Email,
    companyId: projectId ?? company.companyId,
    allPages: allEmailAssociations,
    navigateToPage: navigateToEmail,
    navigateToList: navigateToList,
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { isChatOpen, setIsChatOpen, chatData, setChatData } = useChatContext();

  useChatTrigger(); // Register the global chat trigger

  const handleChatDataUsed = useCallback(() => {
    if (chatData) {
      setChatData({
        ...chatData,
        elementContext: '', // Reset only elementContext
      });
    }
  }, [setChatData, chatData]);

  const sidebarItems = useMemo((): ItemType[] => {
    if (!emailPages?.length) {
      return [];
    }

    // Flat list (same structure as meeting pages)
    return emailPages.flatMap((page) =>
      page.associations.map((association) => ({
        label: (
          <EmailListItemContent>
            <EmailListItemTitle>{association.title}</EmailListItemTitle>
            <EmailListItemDescription>
              {association.updatedAt
                ? humanise(moment(association.updatedAt))
                : ''}
            </EmailListItemDescription>
          </EmailListItemContent>
        ),
        key: String(association.documentId),
        icon: <FontAwesomeIcon icon={faPage} size={12} />,
      })),
    );

    // Legacy: grouped by title with children (for get-pages-grouped API)
    // const groupedByTitles = emailPages.reduce(
    //   (acc, page) => {
    //     acc[page.title] = {
    //       key: page.title,
    //       icon: <FontAwesomeIcon icon={faFolder} size={12} />,
    //       label: page.title,
    //       children: page.associations.map((association) => ({
    //         label: <Title>{association.title}</Title>,
    //         key: association.documentId,
    //         icon: <FontAwesomeIcon icon={faPage} size={12} />,
    //       })),
    //     };
    //     return acc;
    //   },
    //   {} as Record<string, ItemType>,
    // );
    // return Object.values(groupedByTitles);
  }, [emailPages]);

  const selectedAssociationKey =
    emailPage?.metadata?.documentId?.toString() || '';

  // No hierarchy: openKeys not needed (kept for revert if we restore grouped UI)
  // const openKey = React.useMemo(() => { ... }, [sidebarItems, selectedAssociationKey]);
  // const [openKeys, setOpenKeys] = useState<string[]>(openKey ? [openKey] : []);
  // React.useEffect(() => { setOpenKeys(openKey ? [openKey] : []); }, [openKey]);

  // Close chat sidebar when switching between email pages
  React.useEffect(() => {
    setIsChatOpen(false);
  }, [selectedAssociationKey, setIsChatOpen]);

  const searchPlaceholder = useTranslation('search');
  const SideBar = useMemo(() => {
    return (
      <>
        <SearchInputExpanded
          placeholder={searchPlaceholder}
          value={value}
          onChange={onChange}
          onClear={clear}
        />
        <div className="flex flex-col">
          <SidebarMenu
            items={sidebarItems}
            onSelect={({ key }) => {
              const companyIdForNavigation = projectId ?? company.companyId;

              navigateToEmail(companyIdForNavigation, Number(key));
            }}
            selectedKeys={[selectedAssociationKey]}
            mode="inline"
          />
        </div>
      </>
    );
  }, [
    searchPlaceholder,
    value,
    onChange,
    clear,
    sidebarItems,
    selectedAssociationKey,
    navigateToEmail,
    company.companyId,
    projectId,
  ]);

  if (isEmailPagesLoading) {
    return <PageLoading />;
  }

  return (
    <PageWrapper>
      {isSidebarOpen && <Sidebar>{SideBar}</Sidebar>}

      <PageContent>
        <MainContent>
          <FilterBar>
            <FilterBar.Primary>
              {isEmailPagesLoading || allEmailPages?.length === 0 ? null : (
                <Popover
                  content={
                    isSidebarOpen ? null : (
                      <SidebarInPopover>{SideBar}</SidebarInPopover>
                    )
                  }
                  placement="bottomLeft"
                  destroyTooltipOnHide
                  isDisabled={isSidebarOpen}
                  mouseEnterDelay={0.3}
                >
                  <Button
                    variant="ghost"
                    leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  />
                </Popover>
              )}
            </FilterBar.Primary>
            <SecondaryFilter>
              {emailPage != null && !isEmailPageLoading && (
                <>
                  <MetadataTooltip
                    updatedAt={emailPage.metadata.updatedAt}
                    createdAt={emailPage.metadata.createdAt}
                  />

                  {(!isModuleFed || emailPermissions?.canWriteEmailPages) && (
                    <Button
                      variant="primary"
                      leftIcon={<FontAwesomeIcon icon={faEdit} size={12} />}
                      onClick={() => {
                        const companyIdForNavigation =
                          projectId ?? company.companyId;

                        navigateToEmailEdit(
                          companyIdForNavigation,
                          emailPage.metadata.documentId,
                        );
                      }}
                    >
                      <FormattedMessage id="edit" />
                    </Button>
                  )}

                  {(!isModuleFed || emailPermissions?.canDeleteEmailPages) && (
                    <MoreActionsDropdown
                      menu={{
                        onClick: ({ key }) => {
                          if (key === 'delete') {
                            handleDelete();
                          }
                        },
                        items: [
                          {
                            type: 'group',
                            label: <FormattedMessage id="more_actions" />,
                            children: [
                              {
                                key: 'delete',
                                label: <FormattedMessage id="delete" />,
                                icon: <FontAwesomeIcon icon={faTrash} />,
                                danger: true,
                                disabled: !emailPageDocumentMappingId,
                              },
                            ],
                          },
                        ],
                      }}
                      triggerElement={
                        <Button
                          variant="ghost"
                          leftIcon={
                            <FontAwesomeIcon
                              icon={faEllipsisVertical}
                              size={12}
                            />
                          }
                        />
                      }
                    />
                  )}
                </>
              )}
            </SecondaryFilter>
          </FilterBar>

          {isEmailPageLoading && allEmailPages?.length > 0 ? (
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          ) : emailPage == null ? (
            <RocketlaneEmptyStateRoot>
              <RocketlaneEmptyState
                icon={{ icon: faEnvelope }}
                message={intl.formatMessage({
                  id: 'email_page.empty_state_title',
                })}
                description={intl.formatMessage({
                  id: 'email_page.empty_state_message',
                })}
              />
            </RocketlaneEmptyStateRoot>
          ) : (
            <Document
              isChatOpen={isChatOpen}
              document={emailPage}
              readonly={true}
              pageType={PageType.Email}
              sourceId={
                emailPage?.metadata?.documentSourceMappingModel?.sourceId
              }
              // editorExtensionsConfig={editorExtensionsConfig}
            />
          )}
        </MainContent>
      </PageContent>

      <ChatSidebar
        isOpen={isChatOpen}
        title={emailPage?.metadata.title}
        accountId={company.companyId}
        userId={303}
        chatData={chatData}
        onChatDataUsed={handleChatDataUsed}
        onClose={() => setIsChatOpen(false)}
      />
    </PageWrapper>
  );
};

export const AccountEmails = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
}: AccountEmailsProps) => {
  return (
    <ChatProvider>
      <AccountEmailsContent
        company={company}
        projectId={projectId}
        emailPermissions={emailPermissions}
        isModuleFed={isModuleFed}
      />
    </ChatProvider>
  );
};
